import { CompetitionCategoryQualifications } from './components/competition-category-qualifications';
import { useNormalizedCompetitionRegistrationCategoryListQuery } from '../set-category/queries';
import { CompetitionDivisionsTeams } from '../divisions/components/competition-divisions-teams';
import { ConditionalWrapper } from '~/components/ConditionalWrapper/ConditionalWrapper';
import { CompetitionBasicInfo } from './components/competition-basic-info';
import { useCompetitionDivisionListQuery } from '../divisions/queries';
import { usePartialUpdateCompetitionMutation } from '../mutations';
import { PageLayout } from '~/components/PageLayout/PageLayout';
import { useAuthentication } from '~/hooks/useAuthentication';
import { Tooltip } from '~/components/Tooltip/Tooltip';
import { Button } from '~/components/Button/Button';
import { useCompetitionQuery } from '../queries';
import { useParams } from '~/hooks/useParams';
import { Fragment } from 'react/jsx-runtime';
import { idParamSchema } from '~/schemas';
import { Link } from 'react-router';
import { toast } from 'react-toastify';
import { Menu } from './menu';

export function Page() {
  const { id } = useParams(idParamSchema.parse);

  const { data: divisions } = useCompetitionDivisionListQuery({ competition: id, page_size: 999 });
  const { data: categories = [] } = useNormalizedCompetitionRegistrationCategoryListQuery(id);
  const { data } = useCompetitionQuery(id);
  const { can } = useAuthentication();

  const partialUpdateMutation = usePartialUpdateCompetitionMutation();

  if (!data) {
    return false;
  }

  async function handlePublish() {
    await partialUpdateMutation.mutateAsync({ id, payload: { state: 'PUBLISHED' } });
    toast.success('Tävlingen har publicerats');
  }

  const canPublish = data.is_team_comp && data.state === 'CREATED' && can('edit-competition', id);
  const hasDivisions = (divisions?.results.length ?? 0) > 0;

  return (
    <Fragment>
      <PageLayout.Header>
        <PageLayout.Heading text={data.name} />

        <Button.Group neutral>
          {canPublish && (
            <ConditionalWrapper
              condition={!hasDivisions}
              wrap={child => (
                <Tooltip title="Du behöver minst en lagtävlingsdivision för att publicera tävlingen" as="span">
                  {child}
                </Tooltip>
              )}
            >
              <Button
                type="button"
                onClick={handlePublish}
                isLoading={partialUpdateMutation.isPending}
                disabled={!hasDivisions}
              >
                Publicera tävling
              </Button>
            </ConditionalWrapper>
          )}

          <Link to="registrations">
            <Button type="button">Anmälningar</Button>
          </Link>

          <Menu />
        </Button.Group>
      </PageLayout.Header>

      <PageLayout.Section>
        <CompetitionBasicInfo competition={data} />
      </PageLayout.Section>

      {!data.is_team_comp && categories.length > 0 && (
        <PageLayout.Section>
          <PageLayout.SubHeading>Kvalgränser</PageLayout.SubHeading>
          <CompetitionCategoryQualifications categories={categories} />
        </PageLayout.Section>
      )}

      {data.is_team_comp && (
        <PageLayout.Section>
          <PageLayout.SubHeading style={{ display: 'flex', justifyContent: 'space-between' }}>
            Lagtävlingsdivisioner och lag
            {can('manage-competition-division', id) && (
              <Button.Group align="right" neutral>
                <Link to={`/competitions/${id}/divisions/create`}>
                  <Button type="button">Skapa lagtävlingsdivision</Button>
                </Link>

                {hasDivisions ? (
                  <Link to={`/competitions/${id}/teams/create`}>
                    <Button type="button">Lägg till lag</Button>
                  </Link>
                ) : (
                  <Tooltip title="Du behöver minst en lagtävlingsdivision för att lägga till lag" as="span">
                    <Button type="button" disabled>
                      Lägg till lag
                    </Button>
                  </Tooltip>
                )}
              </Button.Group>
            )}
          </PageLayout.SubHeading>

          <CompetitionDivisionsTeams competitionId={id} />
        </PageLayout.Section>
      )}
    </Fragment>
  );
}
